import { ThemeEvents } from '@theme/events';
import { formatMoney } from '@theme/money-formatting';

const OPEN_CLASS = 'cart-notification--open';
const IMAGE_WIDTH = 360;
const AUTO_CLOSE_DELAY = 5000;

class CartNotification extends HTMLElement {
  /** @type {AbortController | null} */
  #abortController = null;

  /** @type {number | undefined} */
  #closeTimeout;

  /** @type {number | undefined} */
  #autoCloseTimeout;

  connectedCallback() {
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate, { signal });
    document.addEventListener('click', this.#onDocumentClick, { signal });
    document.addEventListener('keyup', this.#onKeyUp, { signal });
    window.addEventListener('resize', this.#position, { signal });
  }

  disconnectedCallback() {
    this.#abortController?.abort();
    if (this.#closeTimeout) clearTimeout(this.#closeTimeout);
    if (this.#autoCloseTimeout) clearTimeout(this.#autoCloseTimeout);
  }

  /**
   * @param {CustomEvent<{ data?: { didError?: boolean, source?: string, addedItem?: AddedItem, itemCount?: number, isLowQty?: boolean, memberPrice?: string } }>} event
   */
  #onCartUpdate = (event) => {
    const data = event.detail.data;

    if (data?.didError || data?.source !== 'product-form-component' || !data.addedItem) return;

    this.#render(data.addedItem, data.itemCount, data);
    this.open();
  };

  open() {
    if (this.#closeTimeout) clearTimeout(this.#closeTimeout);
    if (this.#autoCloseTimeout) clearTimeout(this.#autoCloseTimeout);
    this.hidden = false;
    requestAnimationFrame(() => {
      this.#position();
      this.classList.add(OPEN_CLASS);
      this.querySelector('[data-cart-notification-panel]')?.focus();
      this.#autoCloseTimeout = setTimeout(() => this.close(), AUTO_CLOSE_DELAY);
    });
  }

  close() {
    this.classList.remove(OPEN_CLASS);
    if (this.#closeTimeout) clearTimeout(this.#closeTimeout);
    if (this.#autoCloseTimeout) clearTimeout(this.#autoCloseTimeout);
    this.#closeTimeout = setTimeout(() => {
      if (!this.classList.contains(OPEN_CLASS)) this.hidden = true;
    }, 220);
  }

  /**
   * @param {MouseEvent} event
   */
  #onDocumentClick = (event) => {
    if (!this.classList.contains(OPEN_CLASS)) return;
    const target = event.target;
    if (target instanceof Node && this.contains(target)) return;

    this.close();
  };

  /**
   * @param {KeyboardEvent} event
   */
  #onKeyUp = (event) => {
    if (event.code === 'Escape') this.close();
  };

  #position = () => {
    this.style.setProperty('--cart-notification-right', '0px');
  };

  /**
   * @param {AddedItem} item
   * @param {number | undefined} submittedQuantity
   * @param {{ didError?: boolean, source?: string, addedItem?: AddedItem, itemCount?: number, isLowQty?: boolean, memberPrice?: string, compareAtPrice?: string }} [data]
   */
  #render(item, submittedQuantity, data) {
    const image = this.querySelector('[data-cart-notification-image]');
    const title = this.querySelector('[data-cart-notification-title]');
    const price = this.querySelector('[data-cart-notification-price]');
    const salePrice = this.querySelector('[data-cart-notification-sale-price]');
    const comparePrice = this.querySelector('[data-cart-notification-compare-price]');
    const memberPriceRow = this.querySelector('[data-cart-notification-member-price-row]');
    const memberPrice = this.querySelector('[data-cart-notification-member-price]');
    const total = this.querySelector('[data-cart-notification-total]');
    const quantity = this.querySelector('[data-cart-notification-quantity]');
    const skuRow = this.querySelector('[data-cart-notification-sku-row]');
    const sku = this.querySelector('[data-cart-notification-sku]');
    const colorRow = this.querySelector('[data-cart-notification-color-row]');
    const color = this.querySelector('[data-cart-notification-color]');
    const sizeRow = this.querySelector('[data-cart-notification-size-row]');
    const size = this.querySelector('[data-cart-notification-size]');
    const lowStockRow = this.querySelector('[data-cart-notification-low-stock-row]');
    const lowStockIndicator = this.querySelector('[data-cart-notification-low-stock-indicator]');
    const lowStock = this.querySelector('[data-cart-notification-low-stock]');
    const liveRegion = this.querySelector('[data-cart-notification-live-region]');
    const lowStockNotePosition = this.dataset.lowStockNotePosition || 'size';
    const canShowMemberPrice = this.dataset.customerLoggedIn === 'true';

    const itemQuantity = Number(item.quantity || submittedQuantity || 1);
    const itemPrice = Number(item.final_price ?? item.price ?? item.original_price ?? 0);
    const itemOriginalPrice = Number(item.original_price ?? item.price ?? itemPrice ?? 0);
    const itemVariantCompareAtPrice = Number(item.variant?.compare_at_price ?? item.compare_at_price ?? 0);
    const variantCompareAtPriceValue = data?.compareAtPrice || '';
    const hasSalePrice = itemOriginalPrice !== itemPrice;
    const hasComparePrice = Boolean(variantCompareAtPriceValue) || hasSalePrice || itemVariantCompareAtPrice > itemOriginalPrice;
    const comparePriceValue = variantCompareAtPriceValue
      ? variantCompareAtPriceValue
      : hasSalePrice
        ? this.#formatMoney(itemVariantCompareAtPrice > itemOriginalPrice ? itemVariantCompareAtPrice : itemOriginalPrice)
        : itemVariantCompareAtPrice > itemOriginalPrice
          ? this.#formatMoney(itemVariantCompareAtPrice)
          : '';
    const productTitle = item.product_title || item.title || '';
    const memberPriceValue = data?.memberPrice || '';
    const colorValue = this.#optionValue(item, 'color');
    const sizeValue = this.#optionValue(item, 'size');
    const imageUrl = this.#imageUrl(item);
    const isLowQty = data?.isLowQty === true;
    const lowStockLabel = this.dataset.lowStockLabel || '';

    if (image instanceof HTMLElement) {
      image.innerHTML = imageUrl
        ? `<img src="${this.#escapeAttribute(imageUrl)}" alt="${this.#escapeAttribute(productTitle)}" width="${IMAGE_WIDTH}" loading="lazy">`
        : '';
      image.toggleAttribute('data-empty', !imageUrl);
    }

    if (title) title.textContent = productTitle;
    if (salePrice) salePrice.textContent = this.#formatMoney(itemPrice);
    if (comparePrice instanceof HTMLElement) {
      comparePrice.textContent = hasComparePrice ? comparePriceValue : '';
      comparePrice.toggleAttribute('hidden', !hasComparePrice);
    }
    if (price instanceof HTMLElement) {
      price.toggleAttribute('data-has-compare-price', hasComparePrice);
    }
    if (memberPrice) memberPrice.textContent = canShowMemberPrice ? memberPriceValue || '' : '';
    if (memberPriceRow instanceof HTMLElement) {
      memberPriceRow.toggleAttribute('hidden', !(canShowMemberPrice && memberPriceValue));
    }
    if (total) total.textContent = this.#formatMoney(itemPrice * itemQuantity);
    if (quantity) quantity.textContent = String(itemQuantity);

    this.#setOptionalRow(skuRow, sku, item.sku);
    this.#setOptionalRow(colorRow, color, colorValue);
    this.#setOptionalRow(sizeRow, size, sizeValue);
    if (lowStockRow instanceof HTMLElement) {
      const targetValueElement = this.#lowStockTargetValueElement({
        lowStockNotePosition,
        colorValue,
        sizeValue,
        colorRow,
        sizeRow,
      });

      if (targetValueElement instanceof HTMLElement) {
        targetValueElement.appendChild(lowStockRow);
      }

      lowStockRow.toggleAttribute('hidden', !(isLowQty && targetValueElement instanceof HTMLElement));
    }

    if (lowStock) {
      lowStock.textContent = isLowQty ? lowStockLabel : '';
    }
    if (lowStockIndicator instanceof HTMLElement) {
      lowStockIndicator.toggleAttribute('hidden', !(isLowQty && Boolean(lowStockLabel)));
    }

    if (liveRegion) {
      liveRegion.textContent = `${this.dataset.itemAddedLabel || Theme.translations.added}: ${productTitle}`;
    }
  }

  /**
   * @param {Element | null} row
   * @param {Element | null} valueElement
   * @param {string | null | undefined} value
   */
  #setOptionalRow(row, valueElement, value) {
    const hasValue = Boolean(value);
    row?.toggleAttribute('hidden', !hasValue);
    if (valueElement) valueElement.textContent = value || '';
  }

  /**
   * @param {AddedItem} item
   * @param {string} optionName
   * @returns {string | undefined}
   */
  #optionValue(item, optionName) {
    const options = item.options_with_values || [];
    const option = options.find((entry) => {
      const name = entry.name?.toLowerCase();
      if (!name) return false;
      if (optionName === 'color') return name === 'color' || name === 'colour';
      return name === optionName || name.includes(optionName);
    });
    return option?.value;
  }

  /**
   * @param {{
   *   lowStockNotePosition: string,
   *   colorValue: string | undefined,
   *   sizeValue: string | undefined,
   *   colorRow: Element | null,
   *   sizeRow: Element | null,
   * }} params
   * @returns {HTMLElement | null}
   */
  #lowStockTargetValueElement({ lowStockNotePosition, colorValue, sizeValue, colorRow, sizeRow }) {
    const colorTarget = colorRow instanceof HTMLElement ? colorRow.querySelector('dd') : null;
    const sizeTarget = sizeRow instanceof HTMLElement ? sizeRow.querySelector('dd') : null;

    if (lowStockNotePosition === 'color') {
      if (colorValue && colorTarget instanceof HTMLElement) return colorTarget;
      if (sizeValue && sizeTarget instanceof HTMLElement) return sizeTarget;
      if (colorTarget instanceof HTMLElement) return colorTarget;
      return sizeTarget instanceof HTMLElement ? sizeTarget : null;
    }

    if (sizeValue && sizeTarget instanceof HTMLElement) return sizeTarget;
    if (colorValue && colorTarget instanceof HTMLElement) return colorTarget;
    if (sizeTarget instanceof HTMLElement) return sizeTarget;
    return colorTarget instanceof HTMLElement ? colorTarget : null;
  }

  /**
   * @param {number} value
   * @returns {string}
   */
  #formatMoney(value) {
    const format = this.dataset.moneyFormat || '{{amount}}';
    const currency = this.dataset.currency || 'USD';
    return formatMoney(value, format, currency);
  }

  /**
   * @param {AddedItem} item
   * @returns {string | undefined}
   */
  #imageUrl(item) {
    const image = item.featured_image || item.image;

    if (typeof image === 'string') {
      return this.#sizedImageUrl(image);
    }

    if (image && typeof image === 'object') {
      const source = image.url || image.src;
      if (source) return this.#sizedImageUrl(source);
    }

    return undefined;
  }

  /**
   * @param {string} url
   * @returns {string}
   */
  #sizedImageUrl(url) {
    if (url.includes('width=')) return url;

    try {
      const imageUrl = new URL(url, window.location.origin);
      imageUrl.searchParams.set('width', String(IMAGE_WIDTH));
      return imageUrl.toString();
    } catch (_) {
      return url;
    }
  }

  /**
   * @param {string} value
   * @returns {string}
   */
  #escapeAttribute(value) {
    return value.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  }
}

if (!customElements.get('cart-notification')) {
  customElements.define('cart-notification', CartNotification);
}

/**
 * @typedef {object} AddedItem
 * @property {string} [product_title]
 * @property {string} [title]
 * @property {string} [sku]
 * @property {number} [quantity]
 * @property {number} [final_price]
 * @property {number} [price]
 * @property {number} [original_price]
 * @property {{ compare_at_price?: number, price?: number }} [variant]
 * @property {string | { src?: string, url?: string }} [image]
 * @property {{ src?: string, url?: string }} [featured_image]
 * @property {Array<{ name?: string, value?: string }>} [options_with_values]
 */
